import { memo, useCallback } from 'react';

import type { MetricFormat } from '../config/metrics';
import type { PromoLibraryKpiFormat } from '../config/promoLibraryConfig';
import { EMPTY_VALUE, formatKpiValue } from './utils/formatPromoLibraryValue';
import type { PromoLibraryCellValue } from './types';

export interface AttributeCellProps {
    value: string;
    muted?: boolean;
}

/** Descriptor cell. Muted columns render their text de-emphasised. */
export const AttributeCell = memo(({ value, muted }: AttributeCellProps) => (
    <span className={muted ? 'text-[#9F9FA9]' : 'text-[#52525C]'}>{value || EMPTY_VALUE}</span>
));

AttributeCell.displayName = 'AttributeCell';

export interface KpiCellProps {
    value: PromoLibraryCellValue | number | string | null | undefined;
    format: PromoLibraryKpiFormat;
    /** Precision + prefix/suffix for numeric KPIs; ignored when `format` is `'text'`. */
    valueFormat: MetricFormat;
    /** Renders the baseline + delta alongside the current value. */
    showChange: boolean;
    muted?: boolean;
    /** When present the cell is editable — clicking it fires this callback. */
    onEdit?: () => void;
}

/**
 * Normalises API response shapes into a uniform `PromoLibraryCellValue`.
 * Editable KPIs arrive as `{ current, previous }` — passed through.
 * Non-editable KPIs arrive as plain scalars — wrapped with `previous: null`.
 */
const toCellValue = (value: KpiCellProps['value']): PromoLibraryCellValue => {
    if (value !== null && typeof value === 'object') return value;
    return { current: value ?? null, previous: null };
};

const EDITABLE_CELL_CLASSES =
    'rounded px-1 -mx-1 hover:bg-[#F0F0FF] transition-colors';

export const KpiCell = memo(
    ({ value, format, valueFormat, showChange, muted, onEdit }: KpiCellProps) => {
        const cell = toCellValue(value);
        const currentText = formatKpiValue(cell.current, format, valueFormat);

        const handleClick = useCallback(() => onEdit?.(), [onEdit]);
        const editableProps = onEdit
            ? ({ onClick: handleClick, className: EDITABLE_CELL_CLASSES, role: 'button', tabIndex: 0 } as const)
            : undefined;

        if (!showChange || cell.previous === null || cell.previous === undefined) {
            return (
                <span
                    className={muted ? 'text-[#9F9FA9]' : 'text-[#52525C]'}
                    {...editableProps}
                >
                    {currentText}
                </span>
            );
        }

        const previousText = formatKpiValue(cell.previous, format, valueFormat);

        return (
            <div className="grid grid-cols-2 w-full" {...editableProps}>
                <span className={muted ? 'text-[#9F9FA9]' : 'text-[#52525C]'}>{currentText}</span>
                <span className="text-[#A1A1AA]">{previousText}</span>
            </div>
        );
    },
);

KpiCell.displayName = 'KpiCell';

/**
 * Plain-text tooltip for a KPI cell. Shows the formatted current value (and
 * previous if the cell is editable), so clipped content is still readable.
 */
export const formatCellTooltip = (
    value: PromoLibraryCellValue | number | string | null | undefined,
    format: PromoLibraryKpiFormat,
    valueFormat: MetricFormat,
): string => {
    const cell = toCellValue(value);
    return formatKpiValue(cell.current, format, valueFormat);
};
